import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Info, ZoomIn, ZoomOut } from "lucide-react";
import { BeaconMedia } from "../pages/BeaconMedia";
import { useArt, useCaption, artLine, statusLabel } from "../lib/artwork";
import { ArtStory, InterestedButton } from "./ArtStory";

const Details = ({ it, encKey, accent }) => {
  const art = useArt(it, encKey);
  const cap = useCaption(it, encKey);
  if (!art || it._plain) return null;
  return (
    <div className="font-body text-sm text-zinc-300 space-y-1 max-h-[40vh] overflow-y-auto" data-testid="art-lightbox-details">
      {it.title && <div className="text-white text-base">{it.title}</div>}
      {artLine(art) && <div className="text-zinc-400">{artLine(art)}</div>}
      {cap && <p className="text-zinc-400 whitespace-pre-wrap" data-testid="art-lightbox-caption">{cap}</p>}
      <div className="flex flex-wrap gap-3 text-xs">
        <span style={{ color: accent }}>{statusLabel(art.status)}</span>
        {art.show_price && art.price && <span className="text-zinc-300" data-testid="art-lightbox-price">{art.price}</span>}
      </div>
      <ArtStory it={it} art={art} encKey={encKey} accent={accent} />
      <div className="pt-2"><InterestedButton it={it} art={art} accent={accent} /></div>
    </div>
  );
};

// Full-screen artwork viewer: swipe or arrow keys to move, tap or button to zoom, drag to pan.
const OPEN = [];
export const ArtLightbox = ({ items, index, encKey, accent, onClose, footer }) => {
  const [i, setI] = useState(index);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [live, setLive] = useState(false);
  const [info, setInfo] = useState(true);
  const t = useRef(null);
  const pts = useRef(new Map());
  const zoom = scale > 1.01;
  const it = items[i];
  const plain = !!it?._plain;
  const reset = () => { setScale(1); setPan({ x: 0, y: 0 }); };
  const toggle = () => { if (zoom) reset(); else { setScale(2.5); setPan({ x: 0, y: 0 }); } };
  const go = (d) => { reset(); setI((n) => (n + d + items.length) % items.length); };
  useEffect(() => {
    const me = {};
    OPEN.push(me);
    const k = (e) => { if (OPEN[OPEN.length - 1] !== me) return; if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", k); document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", k); OPEN.splice(OPEN.indexOf(me), 1); if (!OPEN.length) document.body.style.overflow = ""; };
  }, [items.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const gap = () => { const [a, b] = [...pts.current.values()]; return Math.hypot(a.x - b.x, a.y - b.y); };
  const down = (e) => {
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pts.current.size === 2) t.current = { pinch: true, d0: gap(), s0: scale, multi: true };
    else t.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y, moved: false, multi: false };
    setLive(true);
  };
  const move = (e) => {
    if (!pts.current.has(e.pointerId) || !t.current) return;
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const s = t.current;
    if (s.pinch && pts.current.size >= 2) {
      const n = Math.min(4, Math.max(1, s.s0 * (gap() / s.d0)));
      setScale(n); if (n <= 1) setPan({ x: 0, y: 0 });
      return;
    }
    const dx = e.clientX - s.x, dy = e.clientY - s.y;
    if (Math.abs(dx) + Math.abs(dy) > 4) s.moved = true;
    if (zoom) setPan({ x: s.px + dx, y: s.py + dy });
  };
  const up = (e) => {
    if (!pts.current.has(e.pointerId)) return;
    pts.current.delete(e.pointerId);
    const s = t.current;
    if (pts.current.size === 1 && s?.pinch) {
      const [p] = [...pts.current.values()];
      t.current = { x: p.x, y: p.y, px: pan.x, py: pan.y, moved: true, multi: true };
      return;
    }
    if (pts.current.size) return;
    t.current = null; setLive(false);
    if (scale < 1.05) reset();
    if (!s || s.multi) return;
    const dx = e.clientX - s.x;
    if (!zoom && Math.abs(dx) > 50 && items.length > 1) { go(dx < 0 ? 1 : -1); return; }
    if (!s.moved && Math.abs(dx) < 6) toggle();
  };
  const cancel = (e) => { pts.current.delete(e.pointerId); if (!pts.current.size) { t.current = null; setLive(false); } };
  const btn = "w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors";
  return createPortal(
    <div className="fixed inset-0 z-[80] bg-black/95 flex flex-col select-none" data-testid="art-lightbox">
      <div className="flex items-center justify-between p-3 shrink-0">
        <span className="font-body text-xs text-zinc-500" data-testid="art-lightbox-counter">{items.length > 1 ? `${i + 1} / ${items.length}` : ""}</span>
        <div className="flex gap-2">
          <button onClick={toggle} className={btn} aria-label="Zoom" data-testid="art-lightbox-zoom">{zoom ? <ZoomOut size={18} /> : <ZoomIn size={18} />}</button>
          {!plain && <button onClick={() => setInfo((v) => !v)} className={btn} aria-label="Details" data-testid="art-lightbox-info"><Info size={18} /></button>}
          <button onClick={onClose} className={btn} aria-label="Close" data-testid="art-lightbox-close"><X size={18} /></button>
        </div>
      </div>
      <div className="relative flex-1 min-h-0 overflow-hidden touch-none" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={cancel} onPointerLeave={cancel}>
        <div className="w-full h-full flex items-center justify-center" style={{ transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${scale})`, transition: live ? "none" : "transform 200ms ease-out", willChange: "transform", cursor: zoom ? "grab" : "zoom-in" }}>
          {it.src ? <img key={it.id} src={it.src} alt={it.title || ""} className="max-w-full max-h-full object-contain pointer-events-none" data-testid="art-lightbox-img" />
            : <BeaconMedia key={it.id} path={it.media} type="image" encKey={it.enc ? encKey : null} iv={it.media_iv} className="max-w-full max-h-full object-contain pointer-events-none" />}
        </div>
        {items.length > 1 && !zoom && <>
          <button onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()} onClick={() => go(-1)} className={`${btn} absolute left-3 top-1/2 -translate-y-1/2 hidden sm:flex`} aria-label="Previous" data-testid="art-lightbox-prev"><ChevronLeft size={20} /></button>
          <button onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()} onClick={() => go(1)} className={`${btn} absolute right-3 top-1/2 -translate-y-1/2 hidden sm:flex`} aria-label="Next" data-testid="art-lightbox-next"><ChevronRight size={20} /></button>
        </>}
      </div>
      {info && !plain && <div className="shrink-0 p-4 border-t border-white/10 bg-black/60"><Details key={it.id} it={it} encKey={encKey} accent={accent} />{footer && footer(it)}</div>}
    </div>,
    document.body
  );
};
